import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { ApplicationState } from "store";

interface LanguageSwitcherProps {
  onLanguageChange: (language: string) => void;
}

export const LanguageSwitcher = (props: LanguageSwitcherProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const currentLocale = useSelector(
    (state: ApplicationState) => state.ui.locale,
  );

  const languages = [
    { code: "en", label: "English", flag: "🇺🇸" },
    { code: "ar", label: "العربية", flag: "🇪🇬" },
  ];

  const currentLanguage = languages.find((lang) => lang.code === currentLocale);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleLanguageSelect = (languageCode: string) => {
    if (languageCode !== currentLocale) {
      props.onLanguageChange(languageCode);
    }
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-gray-100 transition-colors cursor-pointer"
        onClick={toggleDropdown}
      >
        <span className="text-xl">{currentLanguage?.flag}</span>
        <span className="text-sm font-medium text-gray-700">
          {currentLanguage?.label}
        </span>
        <svg
          className={`w-4 h-4 text-gray-500 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full mt-2 right-0 bg-white shadow-lg rounded-md overflow-hidden min-w-[150px] z-50 border border-gray-200">
          {languages.map((language) => (
            <div
              key={language.code}
              className={`flex items-center gap-3 px-4 py-3 hover:bg-gray-100 cursor-pointer transition-colors ${
                currentLocale === language.code ? "bg-gray-50" : ""
              }`}
              onClick={() => handleLanguageSelect(language.code)}
            >
              <span className="text-xl">{language.flag}</span>
              <span
                className={`text-sm ${
                  currentLocale === language.code
                    ? "font-semibold text-primary"
                    : "text-gray-700"
                }`}
              >
                {language.label}
              </span>
              {currentLocale === language.code && (
                <svg
                  className="w-4 h-4 text-primary ml-auto"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
